import React, {
    useContext, useState, useCallback, useMemo, useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { InlineLoader, Tooltip, Card } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { pageTemplateProps } from '@xengage/gw-portals-wizard-react';

import styles from './DigitalWizardCustPageTemplate.module.scss';
import WizardCustSidebar from '../../components/cpmi_WizardSidebar/WizardCustSidebar';
import MobileWizardSteps from '../../components/MobileWizardSteps/MobileWizardSteps';

import { Button } from '@jutro/legacy/components';

import { Link } from '@jutro/router';

function renderLoader(isLoading) {
    return isLoading ? <InlineLoader loading={isLoading} className="gw-inline-loader" /> : null;
}

function previousLoader(isPreviousLoading) {
    return isPreviousLoading ?<div className={styles.submissionwizardbuttons}>Previous<InlineLoader loading={isPreviousLoading} className="gw-inline-loader" /></div> : "Previous";
}

function cpmi_DigitalWizardPageTemplate(props) {
    const translator = useTranslator();
    const breakpoint = useContext(BreakpointTrackerContext);
    const isPhone = breakpoint === 'phoneWide' || breakpoint === 'phone';
    const [showSidebar, setShowSidebar] = useState(false);
    const {
        children: content,
        onNext,
        isLoadingNext,
        disableNext,
        showNext,
        nextLabel,
        nextButtonTooltip,
        onPrevious,
        isLoadingPrevious,
        disablePrevious,
        showPrevious,
        previousLabel,
        onCancel,
        isLoadingCancel,
        disableCancel,
        showCancel,
        cancelLabel,
        renderContextComponent,
        renderWidgets,
        wizardData,
        wizardSnapshot,
        showContextComponent,
        showWizardHeader,
        customMessageModal
    } = props;

    useEffect(() => {
        switch (breakpoint) {
            case 'phoneWide':
            case 'phone':
                setShowSidebar(false);
                break;
            default:
                setShowSidebar(true);
                break;
        }
    }, [breakpoint]);

    const toggleSideBar = useCallback(() => {
        setShowSidebar(!showSidebar);
    }, [showSidebar]);

    const contextComponent = useCallback(() => {
        return <div className={styles.wizardContext}>{renderContextComponent(wizardSnapshot)}</div>;
    }, [renderContextComponent, wizardSnapshot]);

    const shouldShowButtons = useMemo(() => !(
        showNext === false
        && showPrevious === false
        && showCancel === false
    ), [showCancel, showNext, showPrevious]);

    const isPerformingTransiton = useMemo(() => (
        isLoadingCancel || isLoadingPrevious || isLoadingNext
    ), [isLoadingCancel, isLoadingNext, isLoadingPrevious]);

    const wizardClassName = classNames(styles.wizardMain, {
        [styles.wizardMainShowSidebar]: showSidebar && isPhone
    });

    const nextButton = useMemo(() => (
        <Button
            id="gw-wizard-Next"
            className={styles.nextbutton}
            disabled={disableNext || isPerformingTransiton}
            onClick={onNext}
        >
            {translator(nextLabel)}
            {renderLoader(isLoadingNext)}
        </Button>
    ), [disableNext, isLoadingNext, isPerformingTransiton, nextLabel, onNext, translator]);

    const nextButtonWithTooltip = useMemo(() => (
        <Tooltip placement="bottom" content={nextButtonTooltip}>
            <span>{nextButton}</span>
        </Tooltip>
    ), [nextButton, nextButtonTooltip]);

    const renderNextButton = nextButtonTooltip ? nextButtonWithTooltip : nextButton;
    const buttonContainerClassName = classNames(styles.navTransitionButtons, {
        [styles.navTransitionButtonOnlyNext]: !showPrevious && isPhone
    });

    return (
        <div className={styles.digitalWizard}>
            {showContextComponent && contextComponent()}
            <div className={wizardClassName}>
                {!isPhone || showSidebar ? (
                    <div className={classNames(styles.wizardNavigation, 'hiddenPrintElement')}>
                        <WizardCustSidebar customMessageModal={customMessageModal} />
                        <div className={styles.wizardWidgets}>
                            {renderWidgets(wizardData)}
                        </div>
                    </div>
                ) : null}
                <div className={styles.wizardContent}>
                    {isPhone ? <MobileWizardSteps toggleSideBar={toggleSideBar} /> : null}
                    {showWizardHeader && contextComponent()}
                    <div
                        id="digitalWizardContent"
                        className={styles.wizardContentContainer}
                        fluid
                    >
                        {content}
                        <div className={classNames({ [styles.navButtons]: shouldShowButtons }, 'hiddenPrintElement')}>
                            {showCancel && (
                                <Button
                                    id="gw-wizard-cancel"
                                    className={styles.cancelButton}
                                    disabled={disableCancel || isPerformingTransiton}
                                    type="text"
                                    onClick={onCancel}
                                >
                                    {translator(cancelLabel)}
                                    {renderLoader(isLoadingCancel)}
                                </Button>
                            )}
                            
                            <div className={buttonContainerClassName}>
                                <Link  href="https://www.copperpoint.com/hubfs/6246581/CE-1103_Filing-a-Claim.pdf" target="_blank" className={styles.instrLink}>
                                    Instructions
                                </Link>
                                {showPrevious && (
                                    <Button
                                        id="gw-wizard-previous"
                                        className={styles.previousButton}
                                        disabled={disablePrevious || isPerformingTransiton}
                                        type="outlined"
                                        onClick={onPrevious}
                                    >
                                        {/* {translator(previousLabel)} */}
                                        {previousLoader(isLoadingPrevious)}
                                    </Button>
                                )}
                                {showNext && renderNextButton}
                            </div>
                         
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

cpmi_DigitalWizardPageTemplate.propTypes = {
    ...pageTemplateProps,
    /** a function that accepts wizardData to render a React component */
    renderContextComponent: PropTypes.func,
    /** a function that accepts wizardData to render a React component */
    renderWidgets: PropTypes.func,
    showContextComponent: PropTypes.bool,
    showWizardHeader: PropTypes.bool,
    nextButtonTooltip: PropTypes.string
};

cpmi_DigitalWizardPageTemplate.defaultProps = {
    renderContextComponent: () => null,
    renderWidgets: () => null,
    showContextComponent: true,
    showWizardHeader: false,
    nextButtonTooltip: ''
};

export default cpmi_DigitalWizardPageTemplate;